// Utility per gestire notifiche push nel browser

/**
 * Registra il Service Worker
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.warn('⚠️ Service Worker not supported in this browser');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });
    console.log('✅ Service Worker registered:', registration);
    return registration;
  } catch (error) {
    console.error('❌ Service Worker registration failed:', error);
    return null;
  }
}

/**
 * Richiede il permesso per le notifiche
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.warn('⚠️ Notifications not supported in this browser');
    return 'not-supported';
  }

  // Controlla lo stato attuale
  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  // Richiedi permesso
  const permission = await Notification.requestPermission();
  return permission;
}

/**
 * Converte la chiave VAPID da base64 URL a Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  if (!base64String) {
    throw new Error('VAPID public key is missing. Add VITE_VAPID_PUBLIC_KEY to .env');
  }

  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Crea una Push Subscription
 */
export async function createPushSubscription(registration) {
  try {
    // Ottieni la chiave VAPID pubblica dall'ambiente
    const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;

    if (!vapidPublicKey) {
      throw new Error('VAPID public key not found. Please add VITE_VAPID_PUBLIC_KEY to your .env file');
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true, // Obbligatorio: mostra sempre notifiche
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });

    console.log('✅ Push subscription created:', subscription);
    return subscription;
  } catch (error) {
    console.error('❌ Push subscription failed:', error);
    return null;
  }
}

/**
 * Converte una PushSubscription in formato JSON per inviare al backend
 */
export function subscriptionToJSON(subscription) {
  const keys = subscription.getKey ? {
    p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
    auth: arrayBufferToBase64(subscription.getKey('auth'))
  } : {};

  return {
    endpoint: subscription.endpoint,
    keys: keys
  };
}

/**
 * Converte ArrayBuffer a Base64
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

/**
 * Inizializza tutto il sistema di notifiche push
 */
export async function initializePushNotifications(userId) {
  // 1. Registra Service Worker
  const registration = await registerServiceWorker();
  if (!registration) {
    return { success: false, error: 'Service Worker not available' };
  }

  // 2. Richiedi permesso
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    return { success: false, error: 'Permission denied', permission };
  }

  // 3. Crea subscription
  const subscription = await createPushSubscription(registration);
  if (!subscription) {
    return { success: false, error: 'Failed to create subscription' };
  }

  // 4. Converti in formato JSON per il backend
  const subscriptionJSON = subscriptionToJSON(subscription);

  return { success: true, subscription: subscriptionJSON };
}

/**
 * Ottiene la subscription corrente
 */
export async function getCurrentSubscription() {
  try {
    if (!('serviceWorker' in navigator)) {
      return null;
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      return subscriptionToJSON(subscription);
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error getting subscription:', error);
    return null;
  }
}

/**
 * Rimuove la subscription locale
 */
export async function unsubscribeLocal() {
  try {
    if (!('serviceWorker' in navigator)) {
      return false;
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      const result = await subscription.unsubscribe();
      console.log('✅ Local subscription removed:', result);
      return result;
    }
    
    return true;
  } catch (error) {
    console.error('❌ Error unsubscribing locally:', error);
    return false;
  }
}




